import React from "react";
import moment from "moment";
import Select from "react-select";
import {
    VendorClockSvg,
    VendorPersonSvg,
    VendorTagSvg,
    VendorStoreSvg,
    VendorFlagSvg,
    VendorCalendarSvg,
    VendorForwardSvg,
    VendorReplySvg,
    VendorPinSvg,
    VendorMapSvg,
    VendorTickSvg,
} from "../../../components/CommonSvg";
import {
    API_DATE_FORMAT,
    SELECT_PROPS,
    optionOf,
    statusSuffix,
    prioritySuffix,
} from "./ticketUtils";

/* the picker is portalled to <body>, so its menu cannot be reached from the
   stylesheet block the control lives in - the widths are set here instead.
   Status names run long ("Additional Service/Work Needed"), and in a menu
   pinned to the 142px control they wrapped onto three lines. The menu sizes to
   its longest option and hangs from the right edge, so the extra width grows
   back over the header rather than off the screen. */
const STATUS_MENU_STYLES = {
    ...SELECT_PROPS.styles,
    menu: (base) => ({
        ...base,
        width: "max-content",
        minWidth: "100%",
        maxWidth: 280,
        left: "auto",
        right: 0,
    }),
    option: (base) => ({ ...base, whiteSpace: "nowrap" }),
};

const TicketDetailHead = ({
    ticket,
    statusOptions,
    canUpdateTickets,
    hasMultipleVendors,
    canShareTickets,
    onBack,
    onOpenHistory,
    onStatusChange,
    onForward,
    onReply,
}) => {
    /* a status the list carries but the status master does not still has to
       show in the picker, otherwise the control reads as empty */
    const currentStatusOption =
        optionOf(statusOptions, ticket.status) ||
        (ticket.status ? { value: ticket.status, label: ticket.status } : null);

    const isActiveMember = ticket.membershipStatus === "Active";
    /* each tone tints its icon disc - see .vp-tk-sfact in _vendor-portal.scss */
    const storeFacts = [
        {
            label: "HRA Member ID",
            value: ticket.hraMemberId,
            icon: <VendorPersonSvg size={15} />,
            tone: "blue",
        },
        {
            label: "Store Name",
            value: ticket.storeOnly || ticket.company,
            icon: <VendorStoreSvg size={15} />,
            tone: "purple",
        },
        {
            label: "Address",
            value: ticket.address,
            icon: <VendorPinSvg size={15} />,
            tone: "green",
        },
        {
            label: "Field Manager Territory",
            value: ticket.storeTerritory,
            icon: <VendorMapSvg size={15} />,
            tone: "orange",
        },
        {
            label: "Membership Status",
            value: ticket.membershipStatus && (
                <span
                    className={`vp-status-pill ${
                        isActiveMember ? "st-active" : "st-inactive"
                    }`}
                >
                    <span className="sdot" />
                    {ticket.membershipStatus}
                </span>
            ),
            icon: <VendorTickSvg done={isActiveMember} />,
            tone: isActiveMember ? "status" : "status-off",
        },
    ];

    return (
        <div className="vp-tk-dhead">
            <div className="vp-tk-dtop">
                <button
                    type="button"
                    className="vp-tk-obtn vp-tk-backb"
                    onClick={onBack}
                >
                    ‹
                </button>
                <div className="vp-tk-dleft">
                    <div className="vp-tk-drow1">
                        <span className={`vp-tk-stpill st-${statusSuffix(ticket.status)}`}>
                            <span className="sdot" />
                            {ticket.status || "—"}
                        </span>
                        <span className="vp-tk-chip">#{ticket.no}</span>
                    </div>

                    <div className="vp-tk-dsubject">{ticket.subj}</div>
                </div>
                <div className="vp-tk-dactions">
                    <button
                        type="button"
                        className="vp-tk-hist-btn"
                        onClick={onOpenHistory}
                    >
                        <VendorClockSvg />
                        Activities
                    </button>
                    {canUpdateTickets && (
                        <Select
                            {...SELECT_PROPS}
                            styles={STATUS_MENU_STYLES}
                            className={`vp-tk-stsel st-${statusSuffix(ticket.status)}`}
                            classNamePrefix="vp-tk-stsel"
                            options={statusOptions}
                            value={currentStatusOption}
                            onChange={onStatusChange}
                            isClearable={false}
                            isSearchable={false}
                            placeholder="Set status"
                        />
                    )}
                </div>
            </div>

            <div className="vp-tk-dfacts">
                <div className="vp-tk-dfact">
                    <VendorPersonSvg size={15} />
                    <span className="vp-tk-dfbody">
                        <i>Created by</i>
                        <b>{ticket.createdBy || "—"}</b>
                    </span>
                </div>

                {ticket.category && (
                    <div className="vp-tk-dfact">
                        <VendorTagSvg size={15} />
                        <span className="vp-tk-dfbody">
                            <i>Category</i>
                            <b>{ticket.category}</b>
                        </span>
                    </div>
                )}

                {hasMultipleVendors && ticket.vendorName && (
                    <div className="vp-tk-dfact">
                        <VendorStoreSvg size={15} />
                        <span className="vp-tk-dfbody">
                            <i>Vendor</i>
                            <b>{ticket.vendorName}</b>
                        </span>
                    </div>
                )}

                {ticket.priority && (
                    <div
                        className={`vp-tk-dfact is_prio p-${prioritySuffix(
                            ticket.priority
                        )}`}
                    >
                        <VendorFlagSvg size={15} />
                        <span className="vp-tk-dfbody">
                            <i>Priority</i>
                            <b>{ticket.priority}</b>
                        </span>
                    </div>
                )}

                {ticket.createdDate && (
                    <div className="vp-tk-dfact">
                        <VendorCalendarSvg size={15} />
                        <span className="vp-tk-dfbody">
                            <i>Created date</i>
                            <b>
                                {moment(ticket.createdDate, API_DATE_FORMAT).format(
                                    "DD MMM YYYY, h:mm A"
                                )}
                            </b>
                        </span>
                    </div>
                )}

                {/* the two actions travel together at the right end of the
                    strip - the pair is what is pushed over, so the gap between
                    them stays fixed however wide the facts run. A span, not a
                    div: .vp-tk-dfact:last-of-type is what drops the last
                    fact's divider, and a div here would take that :last-of-type
                    for itself. */}
                {(canUpdateTickets || canShareTickets) && (
                    <span className="vp-tk-dactions-end">
                        {canUpdateTickets && (
                            <button
                                type="button"
                                className="vp-tk-obtn vp-tk-reply-btn"
                                onClick={onReply}
                            >
                                <VendorReplySvg size={14} />
                                Reply
                            </button>
                        )}

                        {canShareTickets && (
                            <button
                                type="button"
                                className="vp-tk-obtn vp-tk-fwd-btn"
                                onClick={onForward}
                            >
                                <VendorForwardSvg size={14} />
                                Forward
                            </button>
                        )}
                    </span>
                )}
            </div>

            {/* who the ticket is about - the vendor works the ticket against
                this store, so its details sit above the description rather
                than behind a trip to the Members page */}
            {(ticket.hraMemberId || ticket.company) && (
                <div className="vp-tk-dstore">
                    <h6>Store Details</h6>
                    <div className="vp-tk-sgrid">
                        {storeFacts.map((fact) => (
                            <div
                                key={fact.label}
                                className={`vp-tk-sfact sf-${fact.tone}`}
                            >
                                <span className="vp-tk-sicon">{fact.icon}</span>
                                <span className="vp-tk-sbody">
                                    <i>{fact.label}</i>
                                    <b>{fact.value || "—"}</b>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {ticket.prev && (
                <div className="vp-tk-ddesc">
                    <i>Description</i>
                    <p>{ticket.prev}</p>
                </div>
            )}
        </div>
    );
};

export default TicketDetailHead;
